import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { RangeData } from 'core/models/range.data';
import { RouterBehavior } from 'core/utils/router.utils';
import { Street } from 'dashboard/models/street.model';
import { BuildingsService } from 'dashboard/services/buildings.service';
import { StreetsService } from 'dashboard/services/streets.service';
import { environment } from 'environments/environment';
import {
  EventData,
  GeoJSONSource,
  GeolocateControl,
  Map,
  MapboxGeoJSONFeature,
  MapMouseEvent,
  NavigationControl,
} from 'mapbox-gl';
import { UserService } from 'profile/services/user.service';
import { asyncScheduler, fromEvent, ReplaySubject, Subject } from 'rxjs';
import { filter, first, takeUntil, throttleTime } from 'rxjs/operators';
import { MapStreetsService } from 'streets/components/map/map-streets.service';
import { PurchaseEventData } from 'streets/models/entity-buy-event.data';
import { MapFilterEvent } from 'streets/models/map.data';
import { DynamicComponentService } from 'streets/services/dynamic-component.service';
import { StreetsMapNavState } from 'streets/streets.navigation';

import { MapGeocoderResponse } from '../../models/google-api.data';
import { GeolocationService } from '../../services/geolocation.service';
import { MapService } from '../../services/map.service';
import { MapBuildingsService } from './map-buildings.service';
import { MapHelperService } from './map-helper.service';
import { MapPopupService } from './map-popup.service';
import { MapStateService } from './map-state.service';

@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
  providers: [
    MapHelperService,
    MapBuildingsService,
    MapStreetsService,
    MapStateService,
    MapPopupService,
    DynamicComponentService,
  ],
})
export class MapComponent implements OnInit, OnDestroy, OnChanges {
  get is3D(): boolean {
    return this.show3d || localStorage.getItem('_mps') === '3D';
  }
  set is3D(v: boolean) {
    localStorage.setItem('_mps', v ? '3D' : 'wD');
  }
  @ViewChild('controlsTmpl', { static: true })
  controlsTmpl!: ElementRef<HTMLElement>;
  @ViewChild('mobilePopupTmpl', { static: true })
  mobilePopupTmpl!: ElementRef<HTMLElement>;
  @Input()
  searchInputOnly?: boolean;
  @Input()
  zoom = 11.55;
  @Input()
  cooperativeGestures?: boolean = false;
  @Input()
  pitch?: number = 0;
  @Input()
  show3d?: boolean = false;
  @Input()
  showControls?: boolean = true;
  @Input()
  fitPaddings: [number, number] = [160, 40];
  @Input()
  showSearch?: boolean = true;
  @Input()
  animation = false;
  @Input()
  initialSearch!: { geocoder: MapGeocoderResponse; description?: string };

  @Input()
  repeatMap = false;

  @Output()
  centerChanged = new EventEmitter<{ lat: number; lng: number }>();
  priceRange = { min: 0, max: 100 };
  @ViewChild('mapRef', { static: true })
  private mapRef!: ElementRef<HTMLElement>;

  private renderedMarketsCount = 0;
  private renderedBuildingsMarketsCount = 0;
  private destroy$ = new Subject();
  private render$ = new Subject();
  private load$ = new ReplaySubject();
  private filters: { zones: number[]; price: RangeData } = {
    zones: [],
    price: { min: 0, max: 0 },
  };

  constructor(
    private streetsService: StreetsService,
    private geolocationService: GeolocationService,
    private mapService: MapService,
    private mapStateService: MapStateService,
    private mapStreetsService: MapStreetsService,
    private mapPopupService: MapPopupService,
    private mapBuildingsService: MapBuildingsService,
    private buildingsService: BuildingsService,
    private zone: NgZone,
    private userService: UserService
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    const navState = RouterBehavior.navigationState as StreetsMapNavState;

    if (Object.keys(navState).length) {
      if (navState.streetId) {
        this.streetsService
          .findStreet({ id: navState.streetId })
          .pipe(takeUntil(this.destroy$))
          .subscribe(street => {
            this.mapService.cameraState = {
              lat: street.lat,
              lng: street.lng,
              zoom: 14,
            };
            this.initMapProperties();
            // eslint-disable-next-line rxjs-angular/prefer-takeuntil
            this.load$.pipe(first()).subscribe(() => {
              const openFeature = () => {
                const feature = this.mapStateService.map
                  .queryRenderedFeatures(undefined, { layers: ['streets'] })
                  .filter(f => f.id === street.id)[0];
                if (feature) {
                  this.mapStateService.map.off('sourcedata', openFeature);
                  this.mapStateService.highlightStreet(feature, true);
                  this.mapPopupService.initStreetPopup(
                    feature,
                    false,
                    street.lat,
                    street.lng,
                    this.mobilePopupTmpl,
                    street => {
                      this.mapStreetsService.flyToStreetByGeometry(
                        (street.geometry?.type as 'MultiLineString') || 'LineString',
                        (street.geometry?.coordinates as number[][][]) || [[[]]],
                        [street.lng, street.lat]
                      );
                    }
                  );
                }
              };
              this.mapStateService.map.on('sourcedata', openFeature);
              asyncScheduler.schedule(() => {
                this.mapStateService.map.off('sourcedata', openFeature);
              }, 4000);
            });
          });
      } else if (navState.buildingId) {
        this.buildingsService
          .findBuilding(navState.buildingId)
          .pipe(takeUntil(this.destroy$))
          .subscribe(building => {
            this.mapService.cameraState = {
              lat: building.lat,
              lng: building.lng,
              zoom: 16,
            };
            this.initMapProperties();
            // eslint-disable-next-line rxjs-angular/prefer-takeuntil
            this.load$.pipe(first()).subscribe(() => {
              const openFeature = () => {
                const feature = this.mapStateService.map
                  .queryRenderedFeatures(undefined, { layers: ['buildings'] })
                  .filter(f => f.id === building.id)[0];

                if (feature) {
                  this.mapStateService.map.off('sourcedata', openFeature);
                  // this.mapStateService.(feature, true)
                  this.mapPopupService.initBuildingPopup(
                    feature,
                    false,
                    building.lat,
                    building.lng,
                    this.mobilePopupTmpl,
                    building => {
                      this.mapBuildingsService.flyToBuildingByGeometry([
                        building.lng,
                        building.lat,
                      ]);
                    }
                  );
                }
              };
              this.mapStateService.map.on('sourcedata', openFeature);
              asyncScheduler.schedule(() => {
                this.mapStateService.map.off('sourcedata', openFeature);
              }, 4000);
            });
          });
      }
    } else {
      this.initMapProperties();
    }
    this.mapStateService.updatePadding(this.fitPaddings, this.showSearch);

    this.mapStreetsService
      .onStreetPurchaseEvents$()
      .pipe(
        filter(item => !!item.mapLayer),
        takeUntil(this.destroy$)
      )
      .subscribe();

    this.mapBuildingsService
      .onBuildingPurchaseEvents$()
      .pipe(
        filter(item => !!item.mapLayer),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngOnChanges(changes: SimpleChanges<MapComponent>): void {
    if (changes.fitPaddings) {
      this.mapStateService.updatePadding(this.fitPaddings, this.showSearch);
    }

    if (changes.initialSearch && changes.initialSearch.currentValue) {
      this.selectStreetByGeocode(changes.initialSearch.currentValue);
    }
  }

  filterStreets(event: MapFilterEvent): void {
    if (!this.mapStateService.map?.getSource('streets')) {
      return;
    }
    this.filters = event;
    const zones = ['all', ['in', ['get', 'zone'], event.zones.toString()]];
    const min = ['>=', ['get', 'price'], event.price.min];
    const max = ['<=', ['get', 'price'], event.price.max || 0];
    const isOwn = ['==', ['get', 'owner_id'], this.userService.user.accountId];
    const alwaysShow = ['==', 0, 0];
    this.mapStreetsService.setFilter({ zones, min, max, isOwn, alwaysShow }, event);
    this.mapBuildingsService.setFilters({ zones, min, max, isOwn, alwaysShow }, event, this.is3D);
  }

  selectStreetByGeocode(data: { geocoder: MapGeocoderResponse; description?: string }): void {
    const geometry = data.geocoder.results[0].geometry;
    const isCity = data.geocoder.results[0].types[0] === 'locality';
    const sw = geometry.bounds.getSouthWest();
    const ne = geometry.bounds.getNorthEast();
    this.fitStreetByProperties({
      sw: [sw.lng(), sw.lat()],
      ne: [ne.lng(), ne.lat()],
      applyPadding: !isCity,
      ref: isCity ? undefined : data.geocoder.results[0],
      lat: geometry.location.lat(),
      lng: geometry.location.lng(),
    });
  }

  fitStreetByProperties(props: {
    sw?: [number, number];
    ne?: [number, number];
    applyPadding?: boolean;
    ref?: google.maps.GeocoderResult;
    lat: number;
    lng: number;
  }): void {
    this.mapStateService.popup?.remove();
    this.mapStateService.popup = undefined;
    const findFeature = () => {
      const point = this.mapStateService.map.project([props.lng, props.lat]);
      const feature = this.mapStreetsService.getRoadFeatureAtPoint({
        x: point.x,
        y: point.y,
        extend: 10,
        filters: this.filters,
        reference: {
          lat: props.lat,
          lng: props.lng,
        },
        showSearch: this.showSearch,
      });
      if (feature?.sourceLayer === 'road') {
        this.zone.run(() => {
          this.mapStreetsService.openStreetRequest(feature, props.lat, props.lng, props.ref);
        });
      } else if (feature && feature.properties) {
        this.zone.run(() => {
          this.mapStateService.highlightStreet(feature, true);
          this.mapPopupService.initStreetPopup(
            feature,
            false,
            feature.properties?.lat as number,
            feature.properties?.lon as number,
            this.mobilePopupTmpl
          );
        });
      }
    };
    if (props.sw && props.ne) {
      this.mapStateService.map.fitBounds([props.sw, props.ne], {
        padding: props.applyPadding ? this.mapStateService.fitStreetPadding : 0,
        speed: 2,
        curve: 1,
      });
      // TODO: remove lister if user manually move map
      this.mapStateService.map.once('moveend', findFeature);
    }
  }

  toggleStyle(): void {
    this.is3D = !this.is3D;
    this.mapStateService.toggleStyle(this.is3D, this.pitch);
  }

  private initMapProperties(): void {
    if (this.mapService.cameraState?.lng && this.mapService.cameraState?.lat) {
      const cameraState = this.mapService.cameraState;
      requestAnimationFrame(() => {
        this.createMap(cameraState?.lat, cameraState?.lng, cameraState?.zoom || this.zoom);
      });
    } else {
      const geolocation = this.geolocationService.data;
      const userSaveLocation = this.geolocationService.getMapData();
      if (userSaveLocation && userSaveLocation?.longitude && userSaveLocation?.latitude) {
        this.createMap(
          userSaveLocation.latitude,
          userSaveLocation.longitude,
          userSaveLocation?.zoom || this.zoom
        );
      } else {
        this.createMap(
          geolocation.latitude,
          geolocation.longitude,
          this.mapService.cameraState?.zoom || this.zoom
        );
      }
    }
  }

  private animate(): void {
    this.mapStateService.animate(this.animation);
  }

  private createMap(lat: number, lng: number, zoom: number): void {
    this.mapStateService.map = new Map({
      accessToken: environment.mapboxToken,
      container: this.mapRef.nativeElement,
      style: 'mapbox://styles/gwinbleid/cl3e7edd4000p14ozn17t20kz',
      zoom: zoom,
      minZoom: 1,
      center: [lng, lat],
      pitch: this.is3D ? this.pitch || this.mapService.cameraState?.pitch || 32 : 0,
      bearing: this.mapService.cameraState?.bearing || 0,
      minPitch: 0,
      maxPitch: this.is3D ? 40 : 0,
      attributionControl: false,
      cooperativeGestures: this.cooperativeGestures,
    });

    if (this.showControls) {
      this.mapStateService.map.addControl(
        {
          onAdd: () => this.controlsTmpl.nativeElement,
          onRemove() {},
        },
        'bottom-right'
      );
      this.mapStateService.map.addControl(new GeolocateControl(), 'bottom-right');
      this.mapStateService.map.addControl(
        new NavigationControl({ showCompass: false }),
        'bottom-right'
      );
    }

    if (this.animation) {
      this.animate();
    }

    this.mapStateService.map.on('load', () => {
      this.load$.next();
      const layers = this.mapStateService.map.getStyle().layers;
      // Find the index of the first symbol layer in the map style
      let firstSymbolId;
      if (layers) {
        for (const layer of layers) {
          if (layer.type === 'symbol') {
            firstSymbolId = layer.id;
            break;
          }
        }
      }

      this.mapStateService.initTerrain(this.is3D);
      this.mapStateService.map.setLight({
        color: '#9e9d9d',
        intensity: 0.1,
      });
      this.mapBuildingsService.addSource();
      this.mapStreetsService.addSource();

      this.mapStreetsService.addStreetsLayer(firstSymbolId);
      this.mapStreetsService.addMarketsLayer(firstSymbolId);
      this.mapStreetsService.initUserStreetsClusters(this.userService.user);
      this.mapBuildingsService.addBuildingsLayer(firstSymbolId, this.is3D);
      this.mapBuildingsService.addBuildingsMarketsLayer();
      this.mapBuildingsService.addBuildingsOutlineLayer(firstSymbolId);

      let event: MapMouseEvent & EventData;

      this.mapStateService.map.on('mousemove', e => {
        event = e;
        this.mapStreetsService.handleMousemove(
          event,
          this.filters,
          this.showSearch,
          this.mobilePopupTmpl
        );

        this.mapBuildingsService.handleMouseMove(event, this.mobilePopupTmpl);
      });

      let isTouch = false;
      fromEvent(window, 'touchstart')
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          isTouch = true;
          asyncScheduler.schedule(() => {
            isTouch = false;
          }, 500);
        });
      fromEvent(this.mapRef.nativeElement, 'mouseleave')
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.mapStateService.popupOpenSub.unsubscribe();
          if (this.mapStateService.hoveredFeature) {
            this.mapStateService.highlightStreet(this.mapStateService.hoveredFeature, false);
            this.mapStateService.hoveredFeature = undefined;
          }
        });
      this.mapStateService.map.on('dragstart', () => {
        this.mapStateService.popupOpenSub.unsubscribe();
      });
      this.mapStateService.map.on('moveend', () => {
        this.zone.run(() => {
          const location = this.mapStateService.map.getCenter();
          this.geolocationService.changeState({
            latitude: location.lat,
            longitude: location.lng,
            zoom: this.mapStateService.map.getZoom(),
          });
          this.centerChanged.next(this.mapStateService.map.getCenter());
        });
      });
      this.centerChanged.next(this.mapStateService.map.getCenter());
      const location = this.mapStateService.map.getCenter();
      this.geolocationService.changeState({
        latitude: location.lat,
        longitude: location.lng,
        zoom: this.mapStateService.map.getZoom(),
      });
      this.mapStateService.map.on('render', () => {
        this.render$.next();
      });
      this.render$
        .pipe(throttleTime(600, undefined, { trailing: true }))
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          if (this.animation) {
            return;
          }
          const features = this.getStreetsLayer();
          const buildingFeatures = this.getBuildingsLayer();

          this.drawMarketsByFeatures(features);
          this.drawBuildingsMarketsByFeatures(buildingFeatures);
          this.updatePriceRangeByFeatures(features, buildingFeatures);
        });

      this.mapStateService.map.on('click', 'buildings', e => {
        this.mapBuildingsService.handleClick(e, this.mobilePopupTmpl);
      });
      this.mapStateService.map.on('click', e => {
        this.mapStreetsService.handleClick(
          e,
          isTouch,
          this.filters,
          this.showSearch,
          this.mobilePopupTmpl
        );
      });
    });
    this.mapStateService.map.setRenderWorldCopies(this.repeatMap);
  }

  private updatePriceRangeByFeatures(
    streetFeatures: MapboxGeoJSONFeature[],
    buildingFeatures: MapboxGeoJSONFeature[]
  ): void {
    this.mapService.getPriceRange(streetFeatures, buildingFeatures, res => {
      this.priceRange = { min: res.min, max: res.max };
    });
  }

  private drawBuildingsMarketsByFeatures(features: MapboxGeoJSONFeature[]): void {
    this.mapService.getBuildingsMarketsFeatures(features, res => {
      if (res.features.length !== this.renderedBuildingsMarketsCount) {
        this.renderedBuildingsMarketsCount = res.features.length;

        this.mapBuildingsService.updateBuildingsMarketsData(res);
      }
    });
  }

  private getStreetsLayer(): MapboxGeoJSONFeature[] {
    return this.mapStateService.map.querySourceFeatures('streets', {
      sourceLayer: 'gis_streets',
    });
  }

  private getBuildingsLayer(): MapboxGeoJSONFeature[] {
    return this.mapStateService.map.querySourceFeatures('buildings', {
      sourceLayer: 'buildings',
    });
  }

  private drawMarketsByFeatures(features: MapboxGeoJSONFeature[]): void {
    this.mapService.getMarketsFeatures(features, res => {
      if (res.features.length !== this.renderedMarketsCount) {
        this.renderedMarketsCount = res.features.length;

        (this.mapStateService.map.getSource('markets') as GeoJSONSource).setData(res);
      }
    });
  }

  updateMap(value: PurchaseEventData) {
    const features =
      value.object instanceof Street ? this.getStreetsLayer() : this.getBuildingsLayer();

    const currentFeature = features.find(x => x.id === value.object?.id);
    if (currentFeature && currentFeature.properties) {
      currentFeature.properties.market_id = 999;
    }
    this.mapService.getMarketsFeatures(features, res => {
      (this.mapStateService.map.getSource('markets') as GeoJSONSource).setData(res);
    });
  }
}
